// Copyright 2026 FlairCV Inc.
// SPDX-License-Identifier: LicenseRef-FlairCV-Enterprise

import { FileText, Loader2 } from 'lucide-react';
import { useEffect, useRef } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useAuthStore } from '@/app/stores/auth-store';
import { useCreditStore } from '@/app/stores/credit-store';

/**
 * OAuthCallbackPage handles the redirect from the backend after a successful
 * OAuth authentication. Tokens are passed in the URL fragment (hash) so they
 * never hit the server in a subsequent request.
 *
 * Fragment format:
 *   #access_token=...&refresh_token=...&user_id=...&email=...&credit_balance=...&ai_provider=...&created_at=...
 */
export function OAuthCallbackPage() {
  const router = useRouter();
  const { setTokens, setUser } = useAuthStore();
  const { setBalance } = useCreditStore();
  const processed = useRef(false);

  useEffect(() => {
    // Prevent double-processing in StrictMode.
    if (processed.current) return;
    processed.current = true;

    const hash = window.location.hash.slice(1); // Remove leading '#'
    if (!hash) {
      router.navigate({ to: '/auth/login' });
      return;
    }

    const params = new URLSearchParams(hash);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const userId = params.get('user_id');
    const email = params.get('email');
    const creditBalance = params.get('credit_balance');
    const aiProvider = params.get('ai_provider') as 'platform' | 'anthropic' | 'openai';
    const storageProvider = params.get('storage_provider') ?? 'platform';
    const hasPassword = params.get('has_password') === 'true';

    if (!accessToken || !refreshToken || !userId || !email) {
      router.navigate({ to: '/auth/login' });
      return;
    }

    // Store auth state.
    setTokens(accessToken, refreshToken);
    setUser({
      id: userId,
      email,
      creditBalance: creditBalance ?? '0.0000',
      aiProvider: aiProvider ?? 'platform',
      storageProvider,
      hasPassword,
    });
    setBalance(creditBalance ?? '0.0000');

    // Clear the fragment from the URL (security: don't leave tokens in history).
    window.history.replaceState(null, '', window.location.pathname);

    // Navigate to dashboard.
    router.navigate({ to: '/dashboard' });
  }, [router, setTokens, setUser, setBalance]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="text-center">
        <div className="mb-4 flex items-center justify-center gap-2">
          <FileText className="h-8 w-8 text-primary" />
          <span className="text-[24px] font-semibold tracking-tight">FlairCV</span>
        </div>
        <div className="flex items-center justify-center gap-2 text-[14px] text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Completing sign in...
        </div>
      </div>
    </div>
  );
}
